import React, { useState, FunctionComponent, useEffect } from "react";
import { Box, Paper, Typography, useMediaQuery, useTheme, Button } from "@mui/material";
import { connect } from "react-redux";
import { themeColors } from "../../Components/theme";
import Loading from "../../Components/Loading/Loading";
import styles from "./calculator.module.css";

const scientificButtons = [
    ["(", ")", "MC", "M+", "M-", "MR", "AC", "√", "%", "/"],
    ["2ⁿᵈ", "x²", "x³", "xʸ", "eˣ", "10ˣ", "7", "8", "9", "*"],
    ["1/x", "²√x", "³√x", "ʸ√x", "ln", "log₁₀", "4", "5", "6", "-"],
    ["x!", "sin", "cos", "tan", "EE", "π", "1", "2", "3", "+"],
    ["Deg", "sinh", "cosh", "tanh", "Rand", "0", ".", "Del", "="],
];

type props = {
    theme: any;
};

const Calculator: FunctionComponent<props> = ({ theme }) => {
    const themeMUI = useTheme();
    const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
    const [input, setInput] = useState("");
    const [result, setResult] = useState("");
    const [showLoader, setShowLoader] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setShowLoader(false), 500);
        return () => clearTimeout(timer);
    }, []);

    const handleClick = (value: string) => setInput(input + value);
    const handleClear = () => setInput("");
    const handleBackspace = () => setInput(input.slice(0, -1));
    const handleCalculate = () => {
        try {
            setInput(eval(input).toString()); // ⚠️ Replace eval with a safe parser in production
        } catch {
            setInput("Error");
        }
    };
    const handleScientific = (func: string) => setInput(input + func);

    const handleButton = (btn: string) => {
        if (btn === "AC") handleClear();
        else if (btn === "Del") handleBackspace();
        else if (btn === "=") handleCalculate();
        else if (
            [
                "sin", "cos", "tan", "ln", "log₁₀", "π", "eˣ", "x²", "x³", "xʸ", "√",
                "²√x", "³√x", "ʸ√x", "x!", "sinh", "cosh", "tanh", "Rand", "Deg", "EE",
            ].includes(btn)
        ) {
            handleScientific(btn);
        } else {
            handleClick(btn);
        }
    };

    const containerStyles = {
        backgroundColor: theme
            ? themeColors.backgroundColor
            : themeColors.backgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    };

    const cardContainerStyles = {
        backgroundColor: theme
            ? themeColors.cardBackgroundColor
            : themeColors.cardBackgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    };

    // Dynamic grid columns for mobile vs desktop
    const gridColumns = isMobile ? 5 : 10;

    return (
        <Box
            className={styles.parent}
            sx={{
                ...containerStyles,
                marginTop: isMobile ? "6%" : "4%",
                overflow: "hidden",
                maxHeight: "500px",
                marginBottom: "10px",
            }}
        >
            {showLoader && <Loading />}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    padding: isMobile ? "10px" : "5px 20px",
                }}
            >
                {isMobile && (
                    <h4
                        style={{
                            fontFamily: "var(--font-family-main)",
                            fontSize: "var(--font-size-large)",
                            margin: "0px 0 12px 0",
                        }}
                    >
                        Scientific Calculator
                    </h4>
                )}
                <Box
                    sx={{
                        ...cardContainerStyles,
                        boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
                        borderRadius: 2,
                        width: "100%",
                        padding: "16px",
                    }}
                >
                    <Paper
                        elevation={2}
                        sx={{
                            ...cardContainerStyles,
                            borderRadius: 2,
                            minHeight: isMobile ? 50 : 48,
                            fontSize: isMobile ? 16 : "var(--font-size-large)",
                            textAlign: "right",
                            p: isMobile ? "8px 12px" : "12px 16px",
                            mb: 1,
                            fontFamily: "var(--font-family-main)",
                            overflowX: "auto",
                        }}
                    >
                        {result || input || "0"}
                    </Paper>
                    <Typography
                        color="#888"
                        fontSize={isMobile ? 12 : "var(--font-size-medium)"}
                        textAlign="right"
                        minHeight={24}
                        mb={1.5}
                        fontFamily="var(--font-family-main)"
                    >
                        {input}
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                            gap: isMobile ? 0.5 : 0.75,
                        }}
                    >
                        {scientificButtons.flat().map((btn, idx) => (
                            <Button
                                key={idx}
                                variant={
                                    btn === "=" ? "contained" : btn === "AC" || btn === "Del" ? "outlined" : "text"
                                }
                                color={
                                    btn === "=" ? "primary" : btn === "AC" || btn === "Del" ? "error" : "inherit"
                                }
                                onClick={() => handleButton(btn)}
                                sx={{
                                    height: isMobile ? 50 : 38,
                                    fontSize: isMobile ? 14 : "var(--font-size-medium)",
                                    borderRadius: 1.5,
                                    fontWeight: btn === "=" ? 700 : 500,
                                    fontFamily: "var(--font-family-main)",
                                    gridColumn: btn === "=" || btn === "0" ? "span 2" : undefined,
                                    minWidth: 0,
                                    px: 0,
                                    ...(btn === "=" && {
                                        backgroundColor: "#292744",
                                        color: "#fff",
                                        "&:hover": { backgroundColor: "#1e1b2e" },
                                    }),
                                }}
                            >
                                {btn}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const mapStateToProps = (state: any) => ({
    theme: state.theme.isLightMode,
});

export default connect(mapStateToProps)(Calculator);
